<template>
  <div class="crud-container" data-aos="fade-right">
    <CrudToolbar
      title="Refacciones"
      v-model="filtroTexto"
      searchPlaceholder="Buscar por nombre o proveedor..."
    >
      <template #actions>
        <button class="btn btn-nuevo btn-header" @click="abrirModal(null)">
          <i class="bi bi-plus-lg"></i>
          <span class="btn-header-texto ms-2">Nueva refacción</span>
        </button>
      </template>
    </CrudToolbar>

    <!-- ══════════════════════ TABLA DESKTOP ══════════════════════ -->
    <div class="divisan-table-container shadow-lg d-none d-lg-block">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
        <p class="text-cafe mt-2">Cargando refacciones...</p>
      </div>

      <table v-else class="table table-divisan align-middle mb-0">
        <thead>
          <tr>
            <th class="ps-4">
              <ColumnFilterMenu
                field="nombre_refaccion"
                label="Nombre"
                :filterable="false"
                :items="refaccionesFiltradas"
                :sortField="ordenCampo"
                :sortAsc="ordenAsc"
                @update:sort="onSort"
              />
            </th>
            <th>
              <ColumnFilterMenu
                field="proveedor"
                label="Proveedor"
                :filterable="false"
                :items="refaccionesFiltradas"
                :sortField="ordenCampo"
                :sortAsc="ordenAsc"
                @update:sort="onSort"
              />
            </th>
            <th class="text-end">
              <ColumnFilterMenu
                field="costo"
                label="Costo"
                type="number"
                :filterable="false"
                :items="refaccionesFiltradas"
                :sortField="ordenCampo"
                :sortAsc="ordenAsc"
                @update:sort="onSort"
              />
            </th>
            <th class="text-center">
              <ColumnFilterMenu
                field="disponibilidad_label"
                label="Disponibilidad"
                :sortable="false"
                :items="refaccionesParaFiltro"
                :modelValue="filtroAlmacenSet"
                @update:modelValue="filtroAlmacenSet = $event"
              />
            </th>
            <th class="text-center">Cotización</th>
            <th class="text-center">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="ref in refaccionesPaginadas" :key="ref.id">
            <td class="ps-4">
              <span class="fw-bold text-cafe">{{ ref.nombre_refaccion }}</span>
            </td>
            <td>
              <span class="proveedor-texto">{{ ref.proveedor }}</span>
            </td>
            <td class="text-end">
              <span class="costo-texto">{{ formatCosto(ref.costo) }}</span>
            </td>
            <td class="text-center">
              <span :class="['badge-almacen', ref.en_almacen ? 'en-stock' : 'sin-stock']">
                <i
                  :class="[
                    'bi me-1',
                    ref.en_almacen
                      ? 'bi-check-circle-fill'
                      : 'bi-exclamation-circle-fill',
                  ]"
                ></i>
                {{ ref.en_almacen ? "En almacén" : "Sin stock" }}
              </span>
            </td>
            <td class="text-center">
              <a
                v-if="ref.cotizacion"
                :href="ref.cotizacion"
                target="_blank"
                class="btn-cotizacion"
                title="Ver cotización"
              >
                <i class="bi bi-file-earmark-pdf-fill me-1"></i>Ver PDF
              </a>
              <span v-else class="text-cafe opacity-40 small">—</span>
            </td>
            <td class="text-center">
              <div class="d-flex justify-content-center gap-2">
                <button class="btn-circular" @click="abrirModal(ref)" title="Editar">
                  <i class="bi bi-pencil-square text-verde"></i>
                </button>
                <button
                  class="btn-circular text-danger"
                  @click="confirmarEliminar(ref)"
                  title="Desactivar"
                >
                  <i class="bi bi-trash3-fill"></i>
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="refaccionesPaginadas.length === 0">
            <td colspan="6" class="text-center py-5 text-cafe opacity-50 fw-bold">
              No se encontraron resultados
            </td>
          </tr>
        </tbody>
      </table>

      <div class="paginacion-wrapper">
        <div class="pag-izq">
          <small class="pag-info"
            >{{ rangoInicio }}–{{ rangoFin }} de
            {{ refaccionesOrdenadas.length }} refacciones</small
          >
          <div class="por-pagina-wrap">
            <label class="por-pagina-label">Filas:</label>
            <select
              class="por-pagina-select"
              :value="paginacionStore.porPagina"
              @change="cambiarPorPagina(Number($event.target.value))"
            >
              <option v-for="n in paginacionStore.opcionesPorPagina" :key="n" :value="n">
                {{ n }}
              </option>
            </select>
          </div>
        </div>
        <div class="pag-der">
          <button
            class="btn-pag"
            :disabled="paginaActual === 1"
            @click="paginaActual = 1"
          >
            <i class="bi bi-chevron-double-left"></i>
          </button>
          <button class="btn-pag" :disabled="paginaActual === 1" @click="paginaActual--">
            <i class="bi bi-chevron-left"></i>
          </button>
          <template v-for="p in paginasVisibles" :key="p">
            <span v-if="p === '...'" class="pag-ellipsis">…</span>
            <button
              v-else
              :class="['btn-pag-num', { activo: p === paginaActual }]"
              @click="paginaActual = p"
            >
              {{ p }}
            </button>
          </template>
          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual++"
          >
            <i class="bi bi-chevron-right"></i>
          </button>
          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual = totalPaginas"
          >
            <i class="bi bi-chevron-double-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- ══════════════════════ CARDS MÓVIL ══════════════════════ -->
    <div class="d-lg-none">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
      </div>
      <div
        v-else
        v-for="ref in refaccionesPaginadas"
        :key="'mob-' + ref.id"
        class="divisan-mobile-card mb-3 p-3 shadow-sm"
      >
        <div class="d-flex justify-content-between align-items-start mb-2">
          <span class="fw-bold text-cafe fs-6">{{ ref.nombre_refaccion }}</span>
          <span :class="['badge-almacen', ref.en_almacen ? 'en-stock' : 'sin-stock']">
            {{ ref.en_almacen ? "En almacén" : "Sin stock" }}
          </span>
        </div>
        <div class="row g-2 mb-2">
          <div class="col-6">
            <label class="small text-muted d-block">Proveedor</label>
            <span class="proveedor-texto">{{ ref.proveedor }}</span>
          </div>
          <div class="col-6">
            <label class="small text-muted d-block">Costo</label>
            <span class="costo-texto">{{ formatCosto(ref.costo) }}</span>
          </div>
        </div>
        <div v-if="ref.cotizacion" class="mb-2">
          <a :href="ref.cotizacion" target="_blank" class="btn-cotizacion">
            <i class="bi bi-file-earmark-pdf-fill me-1"></i>Ver cotización
          </a>
        </div>
        <div class="d-flex justify-content-end gap-3 pt-2 border-top">
          <i class="bi bi-pencil-square text-verde fs-5" @click="abrirModal(ref)"></i>
          <i
            class="bi bi-trash3-fill text-danger fs-5"
            @click="confirmarEliminar(ref)"
          ></i>
        </div>
      </div>

      <!-- Paginación móvil -->
      <div class="paginacion-mobile">
        <div class="pag-mobile-top">
          <small class="pag-info"
            >{{ rangoInicio }}–{{ rangoFin }} de {{ refaccionesOrdenadas.length }}</small
          >
          <div class="por-pagina-wrap">
            <label class="por-pagina-label">Filas:</label>
            <select
              class="por-pagina-select"
              :value="paginacionStore.porPagina"
              @change="cambiarPorPagina(Number($event.target.value))"
            >
              <option v-for="n in paginacionStore.opcionesPorPagina" :key="n" :value="n">
                {{ n }}
              </option>
            </select>
          </div>
        </div>
        <div class="pag-mobile-nav">
          <button
            class="btn-pag-mobile"
            :disabled="paginaActual === 1"
            @click="paginaActual--"
          >
            <i class="bi bi-chevron-left"></i> Anterior
          </button>
          <small class="text-verde fw-bold"
            >{{ paginaActual }} / {{ totalPaginas }}</small
          >
          <button
            class="btn-pag-mobile"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual++"
          >
            Siguiente <i class="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- ══════════════════════ MODAL CREAR / EDITAR ══════════════════════ -->
    <div class="modal fade" id="modalRefaccion" tabindex="-1" ref="modalElement">
      <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-0">
            <h5 class="fw-bold text-cafe">
              {{ editando ? "Editar refacción" : "Nueva refacción" }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <!-- modal-form-scrollable: display:contents para no romper el flex de Bootstrap -->
          <form @submit.prevent="guardarRefaccion" class="modal-form-scrollable">
            <div class="modal-body">
              <div class="row g-3">
                <!-- Nombre -->
                <div class="col-12 col-md-8">
                  <label class="form-label label-divisan">Nombre de la refacción</label>
                  <input
                    v-model="form.nombre_refaccion"
                    type="text"
                    class="form-control divisan-input-modal"
                    placeholder="Ej. Rodamiento SKF 6205-2Z"
                    required
                  />
                </div>

                <!-- Costo -->
                <div class="col-12 col-md-4">
                  <label class="form-label label-divisan">Costo (MXN)</label>
                  <div class="input-prefix-wrapper">
                    <span class="input-prefix">$</span>
                    <input
                      v-model.number="form.costo"
                      type="number"
                      step="0.01"
                      min="0"
                      class="form-control divisan-input-modal ps-4-prefix"
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>

                <!-- Proveedor -->
                <div class="col-12">
                  <label class="form-label label-divisan">Proveedor</label>
                  <input
                    v-model="form.proveedor"
                    type="text"
                    class="form-control divisan-input-modal"
                    placeholder="Ej. SKF México S.A. de C.V."
                    required
                  />
                </div>

                <!-- Switch en almacén -->
                <div class="col-12">
                  <hr class="border-verde" />
                  <div class="almacen-switch-row">
                    <div>
                      <span class="label-divisan d-block">¿Disponible en almacén?</span>
                      <small class="text-cafe opacity-75">
                        Si no está en almacén, la cotización es obligatoria.
                      </small>
                    </div>
                    <div class="form-check form-switch mb-0">
                      <input
                        class="form-check-input switch-verde"
                        type="checkbox"
                        id="switchAlmacen"
                        v-model="form.en_almacen"
                        role="switch"
                      />
                      <label
                        class="form-check-label label-divisan ms-2"
                        for="switchAlmacen"
                      >
                        {{ form.en_almacen ? "Sí, disponible" : "No disponible" }}
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Cotización — obligatoria si NO está en almacén -->
                <div class="col-12">
                  <label class="form-label label-divisan">
                    <i class="bi bi-file-earmark-pdf me-1"></i>
                    Cotización (PDF)
                    <span v-if="!form.en_almacen" class="badge-requerido ms-1"
                      >Requerida</span
                    >
                    <span v-else class="text-cafe opacity-50 small ms-1">(opcional)</span>
                  </label>

                  <!-- Si ya existe cotización en modo edición -->
                  <div
                    v-if="editando && cotizacionActual && !nuevaCotizacion"
                    class="cotizacion-actual mb-2"
                  >
                    <i class="bi bi-file-earmark-check-fill text-verde me-1"></i>
                    <a
                      :href="cotizacionActual"
                      target="_blank"
                      class="text-verde fw-bold small"
                    >
                      Ver cotización actual
                    </a>
                    <button
                      type="button"
                      class="btn-reemplazar ms-3"
                      @click="nuevaCotizacion = true"
                    >
                      Reemplazar
                    </button>
                  </div>

                  <input
                    v-if="!editando || nuevaCotizacion || !cotizacionActual"
                    ref="fileInputRef"
                    type="file"
                    @change="handleCotizacion"
                    accept="application/pdf"
                    class="form-control divisan-input-modal"
                    :required="!form.en_almacen && !cotizacionActual"
                  />

                  <small v-if="!form.en_almacen" class="text-danger fw-bold mt-1 d-block">
                    <i class="bi bi-exclamation-triangle-fill me-1"></i>
                    Sin cotización no se puede guardar esta refacción.
                  </small>
                </div>
              </div>
            </div>
            <div class="modal-footer border-0 pt-0">
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm"
                data-bs-dismiss="modal"
              >
                Cancelar
              </button>
              <button type="submit" class="btn btn-nuevo py-2 px-4" :disabled="guardando">
                <span
                  v-if="guardando"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                {{ editando ? "Actualizar" : "Guardar refacción" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import {
  getRefacciones,
  crearRefaccion,
  actualizarRefaccion,
  eliminarRefaccion,
} from "@/utils/api_backend";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import { usePaginacionRefaccionesStore } from "@/store/paginacionStore";
import CrudToolbar from "@/components/Shared/CrudToolbar.vue";
import ColumnFilterMenu from "@/components/Shared/ColumnFilterMenu.vue";

const paginacionStore = usePaginacionRefaccionesStore();

// ── Estado ─────────────────────────────────────────────────
const refacciones = ref([]);
const loading = ref(false);
const guardando = ref(false);
const editando = ref(false);
const nuevaCotizacion = ref(false);
const cotizacionActual = ref(null);
const selectedCotizacion = ref(null);

const filtroTexto = ref("");
const filtroAlmacenSet = ref(new Set());
const paginaActual = ref(1);
const ordenCampo = ref("nombre_refaccion");
const ordenAsc = ref(true);

function onSort({ field, asc }) {
  ordenCampo.value = field;
  ordenAsc.value = asc;
  paginaActual.value = 1;
}

const fileInputRef = ref(null);
const modalElement = ref(null);
let modalInstance = null;

const formVacio = () => ({
  id: null,
  nombre_refaccion: "",
  proveedor: "",
  costo: "",
  en_almacen: false,
  activo: true,
});
const form = ref(formVacio());

// ── Watchers ───────────────────────────────────────────────
watch([filtroTexto, filtroAlmacenSet], () => {
  paginaActual.value = 1;
});

// ── Computed ───────────────────────────────────────────────
const refaccionesBuscadas = computed(() => {
  const q = filtroTexto.value.toLowerCase();
  return refacciones.value.filter(
    (r) =>
      !q ||
      r.nombre_refaccion.toLowerCase().includes(q) ||
      (r.proveedor || "").toLowerCase().includes(q)
  );
});

const refaccionesParaFiltro = computed(() =>
  refaccionesBuscadas.value.map((r) => ({
    ...r,
    disponibilidad_label: r.en_almacen ? "En almacén" : "Sin stock",
  }))
);

const refaccionesFiltradas = computed(() =>
  refaccionesParaFiltro.value.filter(
    (r) =>
      !filtroAlmacenSet.value.size || filtroAlmacenSet.value.has(r.disponibilidad_label)
  )
);

const refaccionesOrdenadas = computed(() =>
  [...refaccionesFiltradas.value].sort((a, b) => {
    let va = a[ordenCampo.value];
    let vb = b[ordenCampo.value];
    if (ordenCampo.value === "costo") {
      va = parseFloat(va) || 0;
      vb = parseFloat(vb) || 0;
      return ordenAsc.value ? va - vb : vb - va;
    }
    va = (va || "").toString().toLowerCase();
    vb = (vb || "").toString().toLowerCase();
    if (va < vb) return ordenAsc.value ? -1 : 1;
    if (va > vb) return ordenAsc.value ? 1 : -1;
    return 0;
  })
);

const totalPaginas = computed(() =>
  Math.max(1, Math.ceil(refaccionesOrdenadas.value.length / paginacionStore.porPagina))
);

const refaccionesPaginadas = computed(() => {
  const ini = (paginaActual.value - 1) * paginacionStore.porPagina;
  return refaccionesOrdenadas.value.slice(ini, ini + paginacionStore.porPagina);
});

const rangoInicio = computed(() =>
  refaccionesOrdenadas.value.length === 0
    ? 0
    : (paginaActual.value - 1) * paginacionStore.porPagina + 1
);
const rangoFin = computed(() =>
  Math.min(
    paginaActual.value * paginacionStore.porPagina,
    refaccionesOrdenadas.value.length
  )
);
const paginasVisibles = computed(() => {
  const total = totalPaginas.value;
  const actual = paginaActual.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, actual]);
  if (actual > 1) set.add(actual - 1);
  if (actual < total) set.add(actual + 1);
  const ordenadas = [...set].sort((a, b) => a - b);
  const resultado = [];
  for (let i = 0; i < ordenadas.length; i++) {
    if (i > 0 && ordenadas[i] - ordenadas[i - 1] > 1) resultado.push("...");
    resultado.push(ordenadas[i]);
  }
  return resultado;
});

function cambiarPorPagina(n) {
  paginacionStore.setPorPagina(n);
  paginaActual.value = 1;
}

function cambiarPagina(p) {
  if (p >= 1 && p <= totalPaginas.value) paginaActual.value = p;
}

// ── Helpers ────────────────────────────────────────────────
function formatCosto(valor) {
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(
    valor || 0
  );
}

// ── Carga de datos ─────────────────────────────────────────
async function cargarRefacciones() {
  loading.value = true;
  try {
    refacciones.value = await getRefacciones();
  } catch {
    Swal.fire("Error", "No se pudo conectar con el servidor", "error");
  } finally {
    loading.value = false;
  }
}

// ── Modal ──────────────────────────────────────────────────
function abrirModal(refaccion = null) {
  selectedCotizacion.value = null;
  nuevaCotizacion.value = false;

  if (fileInputRef.value) fileInputRef.value.value = "";

  if (refaccion) {
    editando.value = true;
    cotizacionActual.value = refaccion.cotizacion || null;
    form.value = {
      id: refaccion.id,
      nombre_refaccion: refaccion.nombre_refaccion,
      proveedor: refaccion.proveedor,
      costo: refaccion.costo,
      en_almacen: refaccion.en_almacen,
    };
  } else {
    editando.value = false;
    cotizacionActual.value = null;
    form.value = formVacio();
  }
  modalInstance?.show();
}

function handleCotizacion(e) {
  selectedCotizacion.value = e.target.files[0] || null;
}

async function guardarRefaccion() {
  if (!form.value.en_almacen && !selectedCotizacion.value && !cotizacionActual.value) {
    Swal.fire({
      icon: "warning",
      title: "Cotización requerida",
      text: "Si la refacción no está en almacén, debes adjuntar una cotización en PDF.",
      confirmButtonColor: "var(--d-brown)",
    });
    return;
  }

  guardando.value = true;
  try {
    const payload = {
      nombre_refaccion: form.value.nombre_refaccion,
      proveedor: form.value.proveedor,
      costo: form.value.costo,
      en_almacen: form.value.en_almacen,
      // activo: form.value.activo,
    };
    if (selectedCotizacion.value) payload.cotizacion = selectedCotizacion.value;

    if (editando.value) {
      await actualizarRefaccion(form.value.id, payload);
    } else {
      await crearRefaccion(payload);
    }

    await cargarRefacciones();
    modalInstance.hide();
    Swal.fire({
      icon: "success",
      title: "Operación exitosa",
      text: `Refacción ${editando.value ? "actualizada" : "creada"} correctamente`,
      timer: 2000,
      showConfirmButton: false,
    });
  } catch (err) {
    const msg = err?.response?.data
      ? Object.values(err.response.data).flat().join(" ")
      : "Ocurrió un problema al procesar la solicitud.";
    Swal.fire("Error", msg, "error");
  } finally {
    guardando.value = false;
  }
}

// ── Eliminar ───────────────────────────────────────────────
async function confirmarEliminar(ref) {
  const result = await Swal.fire({
    title: `¿Desactivar "${ref.nombre_refaccion}"?`,
    text: "La refacción no aparecerá en nuevas rutinas, pero el historial se conserva.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "var(--d-brown)",
    cancelButtonColor: "#6c757d",
    confirmButtonText: "Sí, desactivar",
    cancelButtonText: "Cancelar",
  });
  if (result.isConfirmed) {
    try {
      await eliminarRefaccion(ref.id);
      await cargarRefacciones();
      Swal.fire({
        icon: "success",
        title: "Desactivada",
        text: "La refacción ha sido deshabilitada.",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire("Error", "No se pudo desactivar la refacción.", "error");
    }
  }
}

// ── Init ───────────────────────────────────────────────────
onMounted(() => {
  cargarRefacciones();
  modalInstance = new Modal(modalElement.value);
});
</script>

<style scoped>
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}
.border-verde {
  border-color: var(--d-green) !important;
}

.btn-nuevo {
  background-color: var(--d-brown);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
  padding: 0.6rem 1.5rem;
  border-radius: 8px;
}
.btn-nuevo:hover {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
}

.divisan-table-container {
  background-color: var(--d-cream);
  border-radius: 15px;
  overflow: hidden;
}
.table-divisan thead th {
  background: var(--d-cream);
  color: var(--d-brown);
  border-bottom: 3px solid var(--d-brown);
  padding: 15px;
  font-weight: 800;
  text-transform: capitalize;
  border-top: none;
}
.table-divisan td {
  border-bottom: 1px solid rgba(96, 108, 56, 0.1);
  padding: 12px;
}

/* ── Textos de tabla ── */
.proveedor-texto {
  color: var(--d-green);
  font-size: 0.88rem;
  font-weight: 600;
}
.costo-texto {
  color: var(--d-brown);
  font-weight: 800;
  font-size: 0.95rem;
  font-family: var(--d-font-mono);
}

/* ── Badge disponibilidad ── */
.badge-almacen {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
}
.badge-almacen.en-stock {
  background-color: #d4edda;
  color: #1a5c2b;
}
.badge-almacen.sin-stock {
  background-color: #fff3cd;
  color: #856404;
}

/* ── Botón cotización ── */
.btn-cotizacion {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 6px;
  background-color: rgba(96, 108, 56, 0.12);
  color: var(--d-green-dark);
  font-size: 0.78rem;
  font-weight: 700;
  text-decoration: none;
  border: 1px solid rgba(96, 108, 56, 0.3);
  transition: background 0.2s;
}
.btn-cotizacion:hover {
  background-color: rgba(96, 108, 56, 0.25);
  color: var(--d-green-dark);
}

/* ── Acciones ── */
.btn-circular {
  background: white;
  border: 1px solid var(--d-green);
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
}
.btn-circular:hover {
  background-color: rgba(96, 108, 56, 0.1);
}

/* ── Paginación ── */
.pagination-wrapper {
  border-top: 1px solid rgba(64, 34, 8, 0.1);
  background-color: rgba(96, 108, 56, 0.05);
}
.btn-pag {
  background: white;
  border: 1px solid var(--d-green);
  color: var(--d-brown);
  border-radius: 8px;
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}
.btn-pag:hover:not(:disabled) {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.btn-pag:disabled {
  opacity: 0.35;
  cursor: default;
}

/* ── Modal ── */
.divisan-modal {
  background-color: var(--d-cream);
  border: 2px solid var(--d-brown);
  border-radius: 20px;
}
.label-divisan {
  color: var(--d-brown);
  font-weight: 700;
}
.divisan-input-modal {
  border: 1px solid var(--d-green);
  border-radius: 8px;
}

/* ── Switch almacén ── */
.almacen-switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: rgba(96, 108, 56, 0.07);
  border-radius: 10px;
  padding: 0.75rem 1rem;
  border: 1px solid rgba(96, 108, 56, 0.2);
}
.switch-verde {
  width: 2.5em;
  height: 1.3em;
}
.switch-verde:checked {
  background-color: var(--d-green);
  border-color: var(--d-green);
}

/* ── Costo con prefijo ── */
.input-prefix-wrapper {
  position: relative;
}
.input-prefix {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--d-green);
  font-weight: 700;
  z-index: 5;
}
.ps-4-prefix {
  padding-left: 24px !important;
}

/* ── Badge requerido ── */
.badge-requerido {
  background-color: #a32d2d;
  color: #fff;
  padding: 2px 7px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 700;
}

/* ── Cotización actual (edición) ── */
.cotizacion-actual {
  display: flex;
  align-items: center;
  background: rgba(96, 108, 56, 0.08);
  border: 1px solid rgba(96, 108, 56, 0.25);
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
}
.btn-reemplazar {
  background: none;
  border: 1px solid var(--d-green);
  color: var(--d-green);
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 2px 8px;
  cursor: pointer;
}
.btn-reemplazar:hover {
  background-color: var(--d-green);
  color: var(--d-cream);
}

/* ── Mobile cards ── */
.divisan-mobile-card {
  background-color: var(--d-cream);
  border-radius: 15px;
  border-left: 6px solid var(--d-green);
}

.btn-header {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

/* ── Form dentro del modal: transparente al flex de modal-content ── */
/*    Sin esto, el <form> rompe el chain flex de Bootstrap scrollable  */
.modal-form-scrollable {
  display: contents;
}

@media (max-width: 991.98px) {
  .btn-header-texto {
    display: none;
  }
  .btn-header {
    width: 36px;
    height: 36px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
  }
}

@media (max-width: 768px) {
  /* ── Modal: anclado al viewport con margen para el navbar ── */
  .modal-dialog {
    margin: 85px auto 0.5rem !important;
    max-width: calc(100vw - 1rem) !important;
    height: calc(100dvh - 85px - 0.5rem);
    max-height: calc(100dvh - 85px - 0.5rem);
  }

  .modal-dialog-scrollable .modal-content {
    height: 100%;
    max-height: 100%;
    border-radius: 16px !important;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .modal-dialog-scrollable .modal-header,
  .modal-dialog-scrollable .modal-footer {
    flex-shrink: 0;
  }

  .modal-dialog-scrollable .modal-body {
    flex: 1 1 auto;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .almacen-switch-row {
    flex-direction: column;
    align-items: flex-start;
  }
}

/* ── Paginación unificada ── */
.paginacion-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  flex-wrap: wrap;
  gap: 8px;
  border-top: 1px solid rgba(64, 34, 8, 0.1);
  background: rgba(96, 108, 56, 0.05);
}
.pag-izq {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.pag-der {
  display: flex;
  align-items: center;
  gap: 4px;
}
.pag-info {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}
.por-pagina-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-select {
  appearance: none;
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
  padding: 3px 26px 3px 10px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--d-brown);
  cursor: pointer;
  outline: none;
  min-width: 58px;
}
.btn-pag-num {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.25);
  color: var(--d-brown);
  border-radius: 8px;
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.82rem;
  font-weight: 700;
}
.btn-pag-num:hover {
  background: rgba(96, 108, 56, 0.1);
  border-color: var(--d-green);
}
.btn-pag-num.activo {
  background: var(--d-brown);
  color: var(--d-cream);
  border-color: var(--d-brown);
  pointer-events: none;
}
.pag-ellipsis {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 32px;
  font-size: 0.85rem;
  color: var(--d-brown);
  opacity: 0.4;
  font-weight: 700;
}
.paginacion-mobile {
  border-top: 1px solid rgba(64, 34, 8, 0.15);
  padding: 10px 4px;
  margin-top: 4px;
}
.pag-mobile-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}
.pag-mobile-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
